import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ContactNavigationState } from "../types/navigation";
import "../styleSheets/contactUs.css";
import { capitalizeFirst, logToConsole } from "../utilities/utilities";

type ContactFormData = {
  name: string;
  email: string;
  phone: string;
  bestTime: string;
  bestTimeZone: string;
  message: string;
  /** Honeypot - visually hidden; only a script fills it. */
  website: string;
};

const EMPTY_FORM_DATA: ContactFormData = {
  name: "",
  email: "",
  phone: "",
  bestTime: "",
  bestTimeZone: "",
  message: "",
  website: "",
};

type ContactFieldErrors = Partial<Record<keyof ContactFormData, string>>;

/** Same-origin: CloudFront routes /api/* to the contact-form API, so there is no CORS. */
const CONTACT_API_PATH = "/api/contact";
/** The API rejects larger bodies (and the WAF in front blocks anything over 8,192 bytes). */
const MAX_CONTACT_BODY_BYTES = 7800;

function utf8ByteLength(text: string): number {
  let bytes = 0;
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4;
  }
  return bytes;
}

function ContactUs() {
  const location = useLocation();
  const { t, i18n } = useTranslation("common");
  const [formData, setFormData] = useState<ContactFormData>(EMPTY_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<ContactFieldErrors>({});
  const [statusMessage, setStatusMessage] = useState<{
    type: "success" | "error" | "info";
    message: string;
  } | null>(null);
  const fieldRefs = useRef<
    Partial<
      Record<
        keyof ContactFormData,
        | HTMLInputElement
        | HTMLSelectElement
        | HTMLTextAreaElement
        | null
      >
    >
  >({});

  useEffect(() => {
    const navigationState =
      (location.state as ContactNavigationState | null) ?? null;
    const presetMessage =
      new URLSearchParams(location.search).get("message") ?? "";
    const contactPrefill = navigationState?.contactPrefill;

    setFormData({
      ...EMPTY_FORM_DATA,
      name: contactPrefill?.name ?? "",
      email: contactPrefill?.email ?? "",
      message: contactPrefill?.message ?? presetMessage,
    });
    setFieldErrors({});
    setStatusMessage(null);
  }, [location.key, location.search, location.state]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    const typedName = name as keyof ContactFormData;
    setFieldErrors((prev) => {
      if (!prev[typedName]) return prev;
      const next = { ...prev };
      delete next[typedName];
      return next;
    });
  };

  const validateForm = (): ContactFieldErrors => {
    const nextErrors: ContactFieldErrors = {};
    const trimmedName = formData.name.trim();
    const trimmedEmail = formData.email.trim();
    const trimmedBestTime = formData.bestTime.trim();
    const trimmedBestTimeZone = formData.bestTimeZone.trim();
    const trimmedMessage = formData.message.trim();
    const trimmedPhone = formData.phone.trim();

    if (!trimmedName) {
      nextErrors.name = t("errorMessage.requiredField");
    }

    if (!trimmedEmail) {
      nextErrors.email = t("errorMessage.requiredField");
    } else if (!/^\S+@\S+\.\S+$/.test(trimmedEmail)) {
      nextErrors.email = t("errorMessage.emailValidation");
    }

    if (!trimmedBestTime) {
      nextErrors.bestTime = t("errorMessage.requiredField");
    }

    if (!trimmedBestTimeZone) {
      nextErrors.bestTimeZone = t("errorMessage.requiredField");
    }

    if (!trimmedMessage) {
      nextErrors.message = t("errorMessage.requiredField");
    }

    if (trimmedPhone && !/^[0-9+().\-\s]{7,}$/.test(trimmedPhone)) {
      nextErrors.phone = t("errorMessage.phoneValidation");
    }

    return nextErrors;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const nextErrors = validateForm();

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      setStatusMessage({
        type: "error",
        message: t("recipient.validation.formErrors", {
          defaultValue: "Please correct the highlighted fields before sending.",
        }),
      });

      const firstInvalidField = Object.keys(nextErrors)[0] as
        | keyof ContactFormData
        | undefined;
      if (firstInvalidField) {
        fieldRefs.current[firstInvalidField]?.focus();
      }
      return;
    }

    // The server builds the email; the browser only sends the fields.
    const body = JSON.stringify({
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      bestTime: formData.bestTime,
      timeZone: formData.bestTimeZone,
      message: formData.message.trim(),
      language: i18n.language?.startsWith("es") ? "es" : "en",
      website: formData.website,
    });
    if (utf8ByteLength(body) > MAX_CONTACT_BODY_BYTES) {
      setStatusMessage({
        type: "error",
        message: t("pageBody.contactUs.messageTooLong"),
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(CONTACT_API_PATH, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
      });
      // Only a JSON {"ok":true} counts. A WAF block reaches the browser as CloudFront's SPA
      // page with a 200, which must not look like success.
      const isJson = response.headers.get("content-type")?.includes("application/json");
      const result = isJson ? await response.json().catch(() => null) : null;

      if (response.ok && result?.ok === true) {
        setStatusMessage({
          type: "success",
          message: t("pageBody.contactUs.messageSent"),
        });
        setFieldErrors({});
        setFormData(EMPTY_FORM_DATA);
      } else {
        logToConsole(
          "error",
          "Contact form submission was not accepted:",
          response.status,
        );
        setStatusMessage({
          type: "error",
          message: t("pageBody.contactUs.messageFailed"),
        });
      }
    } catch (error) {
      logToConsole("error", "Error sending email:", error);
      setStatusMessage({
        type: "error",
        message: t("pageBody.contactUs.messageError"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="section-contact" aria-labelledby="contact-page-title">
      <div className="contact-container">
        <div className="container-left">
          <div className="container-left-content">
            <div className="letter-image" />
            <div className="left-title">
              <h1 id="contact-page-title">
                {t("pageBody.contactUs.getInTouch")}
              </h1>
              <div className="divider" />
              <div className="left-text">
                {t("pageBody.contactUs.contactUsText")}
              </div>
            </div>
          </div>
        </div>
        <div className="contact-form">
          <div
            className="contact-status"
            role={statusMessage?.type === "error" ? "alert" : "status"}
            aria-live={statusMessage?.type === "error" ? "assertive" : "polite"}
            aria-atomic="true"
          >
            {statusMessage?.message}
          </div>

          <form onSubmit={handleSubmit} noValidate>
            <div className="input-container">
              <label className="input-label" htmlFor="contact-name">
                {t("recipient.tableColumn.name")}
              </label>
              <input
                ref={(element) => {
                  fieldRefs.current.name = element;
                }}
                id="contact-name"
                className="input-field"
                type="text"
                name="name"
                autoComplete="name"
                maxLength={100}
                value={formData.name}
                onChange={handleChange}
                aria-invalid={!!fieldErrors.name}
                aria-describedby={
                  fieldErrors.name ? "contact-name-error" : undefined
                }
                required
              />
              {fieldErrors.name ? (
                <div className="input-error" id="contact-name-error">
                  {fieldErrors.name}
                </div>
              ) : null}
            </div>

            <div className="input-container">
              <label className="input-label" htmlFor="contact-email">
                {t("recipient.tableColumn.email")}
              </label>
              <input
                ref={(element) => {
                  fieldRefs.current.email = element;
                }}
                id="contact-email"
                className="input-field"
                type="email"
                name="email"
                autoComplete="email"
                maxLength={254}
                value={formData.email}
                onChange={handleChange}
                aria-invalid={!!fieldErrors.email}
                aria-describedby={
                  fieldErrors.email ? "contact-email-error" : undefined
                }
                required
              />
              {fieldErrors.email ? (
                <div className="input-error" id="contact-email-error">
                  {fieldErrors.email}
                </div>
              ) : null}
            </div>

            <div className="input-container">
              <label className="input-label" htmlFor="contact-phone">
                {t("recipient.tableColumn.phone")}
              </label>
              <input
                ref={(element) => {
                  fieldRefs.current.phone = element;
                }}
                id="contact-phone"
                className="input-field"
                type="tel"
                name="phone"
                autoComplete="tel"
                maxLength={30}
                value={formData.phone}
                onChange={handleChange}
                aria-invalid={!!fieldErrors.phone}
                aria-describedby={
                  fieldErrors.phone ? "contact-phone-error" : undefined
                }
              />
              {fieldErrors.phone ? (
                <div className="input-error" id="contact-phone-error">
                  {fieldErrors.phone}
                </div>
              ) : null}
            </div>

            <div className="input-container">
              <label className="input-label" htmlFor="contact-best-time">
                {t("pageBody.contactUs.bestTimeCall")}
              </label>
              <select
                ref={(element) => {
                  fieldRefs.current.bestTime = element;
                }}
                id="contact-best-time"
                className="input-field"
                name="bestTime"
                value={formData.bestTime}
                onChange={handleChange}
                aria-invalid={!!fieldErrors.bestTime}
                aria-describedby={
                  fieldErrors.bestTime ? "contact-best-time-error" : undefined
                }
                required
              >
                <option value="" disabled>
                  {t("pageBody.contactUs.selectTime")}
                </option>
                <option value="Morning">
                  {t("pageBody.contactUs.amCall")}
                </option>
                <option value="Afternoon">
                  {t("pageBody.contactUs.pmCall")}
                </option>
                <option value="Evening">
                  {t("pageBody.contactUs.lateCall")}
                </option>
                <option value="Anytime">
                  {t("pageBody.contactUs.anytimeCall")}
                </option>
                <option value="No Call">{t("pageBody.contactUs.noCall")}</option>
              </select>
              {fieldErrors.bestTime ? (
                <div className="input-error" id="contact-best-time-error">
                  {fieldErrors.bestTime}
                </div>
              ) : null}
            </div>

            <div className="input-container">
              <label className="input-label" htmlFor="contact-time-zone">
                {t("pageBody.contactUs.yourTimeZone")}
              </label>
              <select
                ref={(element) => {
                  fieldRefs.current.bestTimeZone = element;
                }}
                id="contact-time-zone"
                className="input-field"
                name="bestTimeZone"
                value={formData.bestTimeZone}
                onChange={handleChange}
                aria-invalid={!!fieldErrors.bestTimeZone}
                aria-describedby={
                  fieldErrors.bestTimeZone ? "contact-time-zone-error" : undefined
                }
                required
              >
                <option value="" disabled>
                  {t("pageBody.contactUs.selectTimeZone")}
                </option>
                <option value="Pacific/Midway">Midway (UTC-11:00)</option>
                <option value="Pacific/Honolulu">Honolulu (UTC-10:00)</option>
                <option value="America/Anchorage">Anchorage (UTC-09:00)</option>
                <option value="America/Los_Angeles">
                  Los Angeles (UTC-08:00)
                </option>
                <option value="America/Denver">Denver (UTC-07:00)</option>
                <option value="America/Chicago">Chicago (UTC-06:00)</option>
                <option value="America/New_York">New York (UTC-05:00)</option>
                <option value="America/Halifax">Halifax (UTC-04:00)</option>
                <option value="America/Buenos_Aires">
                  Buenos Aires (UTC-03:00)
                </option>
                <option value="Atlantic/South_Georgia">
                  South Georgia (UTC-02:00)
                </option>
                <option value="Atlantic/Azores">Azores (UTC-01:00)</option>
                <option value="Europe/London">London (UTC+00:00)</option>
                <option value="Europe/Berlin">Berlin (UTC+01:00)</option>
                <option value="Europe/Athens">Athens (UTC+02:00)</option>
                <option value="Europe/Moscow">Moscow (UTC+03:00)</option>
                <option value="Asia/Dubai">Dubai (UTC+04:00)</option>
                <option value="Asia/Karachi">Karachi (UTC+05:00)</option>
                <option value="Asia/Dhaka">Dhaka (UTC+06:00)</option>
                <option value="Asia/Bangkok">Bangkok (UTC+07:00)</option>
                <option value="Asia/Shanghai">Shanghai (UTC+08:00)</option>
                <option value="Asia/Tokyo">Tokyo (UTC+09:00)</option>
                <option value="Australia/Sydney">Sydney (UTC+10:00)</option>
                <option value="Pacific/Noumea">Noumea (UTC+11:00)</option>
                <option value="Pacific/Auckland">Auckland (UTC+12:00)</option>
                <option value="Pacific/Fakaofo">Fakaofo (UTC+13:00)</option>
                <option value="Pacific/Kiritimati">
                  Kiritimati (UTC+14:00)
                </option>
              </select>
              {fieldErrors.bestTimeZone ? (
                <div className="input-error" id="contact-time-zone-error">
                  {fieldErrors.bestTimeZone}
                </div>
              ) : null}
            </div>

            <div className="input-container">
              <label className="input-label" htmlFor="contact-message">
                {capitalizeFirst(t("message.message"))}
              </label>
              <textarea
                ref={(element) => {
                  fieldRefs.current.message = element;
                }}
                id="contact-message"
                className="message-area"
                name="message"
                maxLength={3000}
                value={formData.message}
                onChange={handleChange}
                rows={5}
                aria-invalid={!!fieldErrors.message}
                aria-describedby={
                  fieldErrors.message ? "contact-message-error" : undefined
                }
                required
              />
              {fieldErrors.message ? (
                <div className="input-error" id="contact-message-error">
                  {fieldErrors.message}
                </div>
              ) : null}
            </div>

            {/* Honeypot: off-screen (not display:none, which some bots skip), hidden from
                assistive tech and the tab order. A person never fills it; the server answers a
                filled one exactly like a success and sends nothing. */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-10000px",
                top: "auto",
                width: "1px",
                height: "1px",
                overflow: "hidden",
              }}
            >
              <label htmlFor="contact-website">Website</label>
              <input
                id="contact-website"
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={formData.website}
                onChange={handleChange}
              />
            </div>

            <button
              className="send-button"
              type="submit"
              disabled={isSubmitting}
            >
              <div className="send-icon" />
              <div className="send-button-text">
                {isSubmitting ? `${t("common.sending")}...` : t("common.send")}
              </div>
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

export default ContactUs;
